import React, {useEffect, useMemo, useState} from 'react';
import {withTranslation} from 'react-i18next';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Checkbox} from 'primereact/checkbox';
import {InputNumber} from 'primereact/inputnumber';
import {Button} from 'primereact/button';
import {Skeleton} from 'primereact/skeleton';
import axios from 'axios';

const ReceiptAdvanceSettlementComponent = ({receiptId, receipt, fulfillmentId, onChanged, t}) => {
  const [summary, setSummary] = useState(null);
  const [applications, setApplications] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const apiCall = (method, url, data) => axios({
    method,
    url: process.env.REACT_APP_API_HOST + url,
    data,
    headers: {
      Authorization: 'Bearer ' + localStorage.getItem('userToken'),
      'App-Network': localStorage.getItem('networkId')
    }
  });

  const formatCurrency = (amount, currency) => window.App.currencyFormatter(
    currency || receipt?.currency || process.env.REACT_APP_CURRENCY_DEFAULT,
    Number(amount || 0)
  );

  const load = () => {
    if (!receiptId) return;
    setLoading(true);
    const query = fulfillmentId ? `?fulfillment_id=${fulfillmentId}` : '';
    apiCall('get', `/api/v2/receipt/${receiptId}/advance-settlements/summary${query}`)
      .then(response => {
        const data = response?.data?.data || {};
        setSummary(data);
        const next = {};
        (data.settlements || []).forEach(row => {
          if (row.advance_receipt?.id) next[row.advance_receipt.id] = Number(row.gross_amount || 0);
        });
        setApplications(next);
      })
      .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response))
      .finally(() => setLoading(false));
  };

  useEffect(load, [receiptId, fulfillmentId]);

  const rows = useMemo(() => {
    const map = new Map();
    (summary?.eligible_advances || []).forEach(row => map.set(row.id, row));
    (summary?.settlements || []).forEach(settlement => {
      const advance = settlement.advance_receipt;
      if (!advance?.id) return;
      const existing = map.get(advance.id) || {};
      map.set(advance.id, {
        ...existing,
        id: advance.id,
        receipt_number: settlement.advance_receipt_number,
        paid_at: settlement.advance_payment_date,
        currency: settlement.currency,
        available_gross_amount: Number(existing.available_gross_amount || 0) + Number(settlement.gross_amount || 0)
      });
    });
    return Array.from(map.values());
  }, [summary]);

  const selectedTotal = Object.values(applications).reduce((sum, amount) => sum + Number(amount || 0), 0);
  const baseGross = Number(summary?.receipt?.base_gross_amount || 0);
  const payable = Math.max(0, baseGross - selectedTotal);
  const editable = receipt?.draft === true && localStorage.getItem('roles')?.includes('role_receipt_write');

  const toggle = (row, checked) => setApplications(current => {
    const next = {...current};
    if (checked) next[row.id] = Math.min(Number(row.available_gross_amount || 0), Math.max(0, baseGross - Object.entries(current).filter(([id]) => Number(id) !== row.id).reduce((sum, [, value]) => sum + Number(value || 0), 0)));
    else delete next[row.id];
    return next;
  });

  const save = () => {
    setSaving(true);
    apiCall('put', `/api/v2/receipt/${receiptId}/advance-settlements`, {
      fulfillment_id: fulfillmentId || null,
      applications: Object.entries(applications).map(([advance_receipt_id, gross_amount]) => ({
        advance_receipt_id: Number(advance_receipt_id), gross_amount: Number(gross_amount)
      }))
    })
      .then(() => {
        window.App.toastShow('success', t('success'), t('advanceSettlementSaved'));
        load();
        if (onChanged) onChanged();
      })
      .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response))
      .finally(() => setSaving(false));
  };

  if (loading && !summary) {
    return <div className="p-p-3"><Skeleton height="3rem" className="p-mb-2"/><Skeleton height="10rem"/></div>;
  }

  return (
    <div className="p-fluid">
      <div className="p-grid p-mb-3">
        <div className="p-col-12 p-md-4">
          <div className="surface-card border-1 surface-border border-round p-p-3">
            <small className="p-text-uppercase text-secondary">{t('invoicePerformanceGross')}</small>
            <div className="p-text-bold p-mt-2">{formatCurrency(baseGross, summary?.receipt?.currency)}</div>
          </div>
        </div>
        <div className="p-col-12 p-md-4">
          <div className="surface-card border-1 surface-border border-round p-p-3">
            <small className="p-text-uppercase text-secondary">{t('appliedAdvance')}</small>
            <div className="p-text-bold p-mt-2 text-primary">{formatCurrency(selectedTotal, summary?.receipt?.currency)}</div>
          </div>
        </div>
        <div className="p-col-12 p-md-4">
          <div className="surface-card border-1 surface-border border-round p-p-3">
            <small className="p-text-uppercase text-secondary">{t('amountPayable')}</small>
            <div className="p-text-bold p-mt-2">{formatCurrency(payable, summary?.receipt?.currency)}</div>
          </div>
        </div>
      </div>

      {rows.length === 0 && (summary?.settlements || []).length === 0 ? (
        <div className="p-message p-message-info">
          <div className="p-message-wrapper"><span className="p-message-icon pi pi-info-circle"/><span className="p-message-text">{t('noAvailableAdvanceInvoices')}</span></div>
        </div>
      ) : (
        <DataTable value={rows} loading={loading} responsiveLayout="scroll" stripedRows dataKey="id" emptyMessage={t('noResults')}>
          {editable && <Column style={{width: '4rem'}} body={row => <Checkbox checked={applications[row.id] !== undefined} onChange={event => toggle(row, event.checked)}/>}/>} 
          <Column field="receipt_number" header={t('columnDocumentNumber2')}/>
          <Column field="paid_at" header={t('advancePaymentDate')} body={row => row.paid_at ? new Intl.DateTimeFormat(undefined).format(new Date(`${row.paid_at}T00:00:00`)) : '—'}/>
          <Column header={t('availableAmount')} className="p-text-right" body={row => formatCurrency(row.available_gross_amount, row.currency)}/>
          <Column header={t('appliedAmount')} className="p-text-right" body={row => editable ? (
            <InputNumber value={applications[row.id] ?? null} disabled={applications[row.id] === undefined} min={0} max={Number(row.available_gross_amount || 0)} minFractionDigits={row.currency === 'HUF' ? 0 : 2} maxFractionDigits={row.currency === 'HUF' ? 0 : 2} onValueChange={event => setApplications(current => ({...current, [row.id]: event.value || 0}))}/>
          ) : formatCurrency(applications[row.id] || 0, row.currency)}/>
        </DataTable>
      )}

      {(summary?.settlements || []).some(row => !row.reconciled) && (
        <div className="p-message p-message-warn p-mt-3">
          <div className="p-message-wrapper"><span className="p-message-icon pi pi-exclamation-triangle"/><span className="p-message-text">{t('unreconciledNavAdvanceHint')}</span></div>
        </div>
      )}

      {editable && (
        <div className="p-d-flex p-jc-end p-mt-3">
          <Button label={t('saveAdvanceSettlements')} icon="pi pi-check" className="p-button-success" loading={saving} onClick={save}/>
        </div>
      )}
    </div>
  );
};

export default withTranslation('common')(ReceiptAdvanceSettlementComponent);
